/**
 * `<policy-selector>` — the shared matcher builder, reused by eight kinds
 * (`SC-F504`, `SC-F505`, `SC-F506`, `SC-F507`, `SC-F509`).
 *
 * The design commitment that matters most: **`any` and `all` are one radio, never
 * two lists.** Populating both is legal upstream and evaluates as
 * `ANY(any) OR ALL(all)` — an OR between the two lists, which is wider than either
 * alone and almost never what the author drew. The radio makes the mistake
 * unrepresentable for anything authored here, and an *imported* object that
 * populates both gets the semantics stated in a blocking banner plus a one-click
 * merge, because refusing to render it would leave a policy nobody can fix.
 *
 * Three more facts are stated inline rather than left to be inferred, because each
 * is a documented authoring mistake:
 *
 *   - A clause with only `resources` matches **every subject, including
 *     anonymous**; a clause with only `subjects` matches **every table**. Both
 *     omissions look like narrowing and are widening.
 *   - Every populated field of a subject selector ANDs with the others, and clauses
 *     within a list OR.
 *   - `ipRanges` is disabled and badged. It is not removed: dropping the value from
 *     an imported policy would make that policy *broader*, and Sluice's own
 *     documentation recommends the field over CEL — while the CEL route is dead too
 *     (`SLUICE-U12`), so there is no alternative to suggest and the editor suggests
 *     none.
 *
 * Every label, help sentence, enum and status comes from the form schema. The
 * widgets are mapped by field name here; the vocabulary never is.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "../../components/index.js";
import type { BnSelectChangeDetail } from "../../components/index.js";
import type { PolicyFinding } from "../../api/policies.js";
import type { BindingSummary } from "./bindings.js";
import {
  emptyClause,
  type ClaimCheck,
  type Clause,
  type ResourceSelector,
  type ScalarValue,
  type Selector,
  type SubjectSelector,
} from "./model.js";
import { resolveClauseTables } from "./patterns.js";
import { diagnosticSeverity, findingsAt, findingsUnder, worst } from "./policy-findings.js";
import "./policy-pattern-list.js";
import type { PatternListChangeDetail, PatternSplitDetail } from "./policy-pattern-list.js";
import "./policy-typed-value.js";
import type { TypedValueChangeDetail } from "./policy-typed-value.js";
import type { PolicySchema } from "./schema.js";
import type { SchemaCache } from "./schema-cache.js";

/** Detail of `selector-change`. */
export interface SelectorChangeDetail {
  selector: Selector;
}

/** Which of the two lists a clause sits in. */
type ClauseList = "any" | "all";

/** The subject fields that are plain string lists — no wildcards, no preview. */
const STRING_LIST_FIELDS = new Set(["groups", "roles", "apiKeys", "ipRanges"]);

/** Which resource fields are wildcard-pattern lists, and which axis of the schema
 * cache each resolves against. */
const PATTERN_SCOPES = {
  catalogs: "catalogs",
  schemas: "schemas",
  tables: "tables",
  columns: "columns",
} as const;

@customElement("policy-selector")
export class PolicySelector extends LitElement {
  /** The form schema. Required — every label and enum is read from it. */
  @property({ attribute: false }) schema!: PolicySchema;
  /** The selector being edited. */
  @property({ attribute: false }) selector: Selector = {};
  /** The object path this selector sits at — `spec.match` or `spec.exclude` — so
   * findings anchor and the heading reads correctly. */
  @property() path = "spec.match";
  /** True for `exclude`, which is optional, collapsed by default, and carries the
   * per-kind scope explainer. */
  @property({ type: Boolean, attribute: "is-exclude" }) isExclude = false;
  /** The kind's own exclude-scope sentence. */
  @property() scopeExplainer = "";
  /** The schema cache, for pattern previews. */
  @property({ attribute: false }) cache: SchemaCache = {
    tables: [],
    degraded: true,
    warning: "",
    fetchedAt: "",
  };
  /** Findings for the whole object; this element filters to its own subtree. */
  @property({ attribute: false }) findings: PolicyFinding[] = [];
  /** Known API key ids, for the `apiKeys` completions. */
  @property({ attribute: false }) bindings: BindingSummary[] = [];
  /** Classification tags defined in this draft, when the caller knows them. An
   * empty list means the editor cannot offer a picker and says so. */
  @property({ attribute: false }) availableTags: string[] = [];

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .mode {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-sm);
    }
    fieldset {
      border: none;
      margin: 0;
      padding: 0;
      display: flex;
      gap: var(--bino-space-md);
    }
    legend {
      font-size: var(--bino-font-size-base);
      font-weight: 500;
      color: var(--bino-text-muted);
      padding: 0;
      margin-bottom: 0.375rem;
    }
    label.radio {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
      cursor: pointer;
    }
    label.radio input {
      accent-color: var(--bino-primary);
    }
    label.radio input:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
      border-radius: var(--bino-radius-sm);
    }
    .note {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .banner {
      border: 1px solid var(--bino-error-border);
      background: var(--bino-error-bg);
      color: var(--bino-text);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .banner p {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-base);
      max-width: 80ch;
    }
    .banner .row {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
    }
    .clauses {
      display: grid;
      gap: var(--bino-space-md);
    }
    .clause {
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
      padding: var(--bino-space-md);
    }
    .clause-head {
      display: flex;
      align-items: baseline;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-sm);
    }
    .clause-title {
      font-size: var(--bino-font-size-base);
      font-weight: 600;
    }
    .spacer {
      flex: 1;
    }
    .half {
      margin-top: var(--bino-space-md);
    }
    .half-head {
      display: flex;
      align-items: baseline;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-xs);
    }
    h4 {
      margin: 0;
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-primary);
    }
    .widening {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-warning-text);
      background: var(--bino-warning-bg);
      border-radius: var(--bino-radius-sm);
      padding: 0 var(--bino-space-xs);
    }
    .resolved {
      margin-top: var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .resolved.none {
      color: var(--bino-warning-text);
      background: var(--bino-warning-bg);
      border-radius: var(--bino-radius-sm);
      padding: var(--bino-space-xs) var(--bino-space-sm);
    }
    .advisory {
      display: block;
      font-size: var(--bino-font-size-xs);
    }
    .summary {
      margin-top: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-muted);
      max-width: 80ch;
    }
    .summary code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .strings .entry {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-xs);
    }
    .strings bn-input {
      flex: 1 1 auto;
      min-width: 0;
    }
    .checks {
      display: grid;
      gap: var(--bino-space-sm);
    }
    .check {
      border: 1px dashed var(--bino-border-light);
      border-radius: var(--bino-radius-sm);
      padding: var(--bino-space-sm);
    }
    .check-grid {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
    }
    .check-grid > * {
      flex: 1 1 12rem;
      min-width: 0;
    }
    bn-field {
      margin-bottom: var(--bino-space-sm);
    }
    .empty {
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
      margin: 0 0 var(--bino-space-sm);
    }
  `;

  /** Which list is active. A selector with neither populated starts at `any`,
   * which is the form every worked example in the specification takes. */
  private get mode(): ClauseList {
    if ((this.selector.all ?? []).length > 0 && (this.selector.any ?? []).length === 0) return "all";
    return "any";
  }

  private get bothPopulated(): boolean {
    return (this.selector.any ?? []).length > 0 && (this.selector.all ?? []).length > 0;
  }

  private emit(selector: Selector) {
    this.dispatchEvent(
      new CustomEvent<SelectorChangeDetail>("selector-change", {
        detail: { selector },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** patch replaces one clause list, dropping the key entirely when it empties so
   * the rendered YAML carries no empty array. */
  private patchList(list: ClauseList, clauses: Clause[]) {
    const next: Selector = { ...this.selector };
    if (clauses.length === 0) delete next[list];
    else next[list] = clauses;
    this.emit(next);
  }

  private patchClause(list: ClauseList, index: number, clause: Clause) {
    const clauses = [...(this.selector[list] ?? [])];
    clauses[index] = clause;
    this.patchList(list, clauses);
  }

  private addClause(list: ClauseList) {
    this.patchList(list, [...(this.selector[list] ?? []), emptyClause()]);
  }

  private removeClause(list: ClauseList, index: number) {
    this.patchList(
      list,
      (this.selector[list] ?? []).filter((_, i) => i !== index),
    );
  }

  /** Switching the radio moves the clauses rather than clearing them: the author is
   * changing how the clauses combine, not discarding them. */
  private setMode(mode: ClauseList) {
    if (mode === this.mode) return;
    const clauses = [...(this.selector.any ?? []), ...(this.selector.all ?? [])];
    this.emit(clauses.length === 0 ? {} : ({ [mode]: clauses } as Selector));
  }

  /** The merge offer on an imported both-populated selector. */
  private merge(into: ClauseList) {
    const clauses = [...(this.selector.any ?? []), ...(this.selector.all ?? [])];
    this.emit({ [into]: clauses } as Selector);
  }

  // ── Clause halves ───────────────────────────────────────────────────────

  private patchSubjects(list: ClauseList, index: number, subjects: SubjectSelector | undefined) {
    const clause: Clause = { ...(this.selector[list]?.[index] ?? {}) };
    if (!subjects || Object.keys(subjects).length === 0) delete clause.subjects;
    else clause.subjects = subjects;
    this.patchClause(list, index, clause);
  }

  private patchResources(list: ClauseList, index: number, resources: ResourceSelector | undefined) {
    const clause: Clause = { ...(this.selector[list]?.[index] ?? {}) };
    if (!resources || Object.keys(resources).length === 0) delete clause.resources;
    else clause.resources = resources;
    this.patchClause(list, index, clause);
  }

  /**
   * setStringList writes one array-of-string field, dropping the key when it empties
   * so the rendered document carries no empty array — an empty list and an absent one
   * mean opposite things in this DSL, and the renderer must not have to guess.
   *
   * It is generic over the holder so the same helper serves both halves of a clause
   * without either being widened to an index signature: `SubjectSelector` and
   * `ResourceSelector` are closed shapes on purpose.
   */
  private setStringList<T extends object>(holder: T, field: string, values: string[]): T {
    const next = { ...holder } as Record<string, unknown>;
    const kept = values.filter((v) => v.trim() !== "");
    if (kept.length === 0) delete next[field];
    else next[field] = kept;
    return next as T;
  }

  // ── Rendering ───────────────────────────────────────────────────────────

  private findingAt(path: string): PolicyFinding | null {
    return worst(findingsAt(this.findings, path));
  }

  /** A plain list of strings — groups, roles, api key ids, CIDR ranges. It is not
   * the pattern field: these are compared literally, so the wildcard grammar and
   * its refusals would be wrong help. */
  private renderStringList(
    label: string,
    help: string,
    values: string[],
    path: string,
    onChange: (values: string[]) => void,
    opts: { disabled?: boolean; badge?: string; reason?: string; completions?: string[] } = {},
  ) {
    const rows = opts.disabled ? (values.length > 0 ? values : []) : [...values, ""];
    const finding = this.findingAt(path);
    return html`<bn-field
      label=${label}
      hint=${help}
      error=${opts.reason ?? finding?.message ?? ""}
      rule=${opts.reason ? "" : (finding?.rule ?? "")}
      severity=${opts.reason
        ? "blocking-warning"
        : finding
          ? diagnosticSeverity(finding.severity)
          : "error"}
    >
      <div class="strings">
        ${opts.badge
          ? html`<bn-badge variant="not-enforced">${opts.badge}</bn-badge>`
          : nothing}
        ${rows.length === 0 && opts.disabled
          ? html`<p class="empty">Not set.</p>`
          : rows.map((v, i) => {
              const last = i === values.length;
              return html`<div class="entry">
                <bn-input
                  mono
                  aria-label=${`${label} ${i + 1}`}
                  placeholder=${last ? "Add…" : ""}
                  .value=${v}
                  ?disabled=${!!opts.disabled}
                  @input=${(e: CustomEvent<{ value: string }>) => {
                    e.stopPropagation();
                    const next = [...values];
                    if (i < next.length) next[i] = e.detail.value;
                    else next.push(e.detail.value);
                    onChange(next);
                  }}
                ></bn-input>
                ${!opts.disabled && !last
                  ? html`<bn-button
                      size="sm"
                      variant="ghost"
                      @click=${() => onChange(values.filter((_, j) => j !== i))}
                      aria-label=${`Remove ${label} ${v}`}
                    >
                      Remove
                    </bn-button>`
                  : nothing}
              </div>`;
            })}
        ${opts.completions && opts.completions.length > 0
          ? html`<p class="note">Defined in this instance: ${opts.completions.join(", ")}.</p>`
          : nothing}
      </div>
    </bn-field>`;
  }

  /** One claim check. The operator is a closed select because Sluice does **not**
   * validate it: `op: equals` compiles cleanly and then never matches, which is the
   * archetypal case for a form (`SC-F506`). */
  private renderClaimCheck(
    check: ClaimCheck,
    index: number,
    basePath: string,
    onChange: (check: ClaimCheck) => void,
    onRemove: () => void,
  ) {
    const group = "claimCheck";
    const opField = this.schema.field(group, "op");
    const valueField = this.schema.field(group, "value");
    const valuesField = this.schema.field(group, "values");
    const patternField = this.schema.field(group, "pattern");
    const siblings = { op: check.op };
    const path = `${basePath}[${index}]`;
    const claimFinding = this.findingAt(`${path}.claim`);
    const opFinding = this.findingAt(`${path}.op`);
    return html`<div class="check">
      <div class="check-grid">
        <bn-field
          label="Claim"
          required
          hint=${this.schema.help(group, "claim")}
          error=${claimFinding?.message ?? ""}
          rule=${claimFinding?.rule ?? ""}
          severity=${claimFinding ? diagnosticSeverity(claimFinding.severity) : "error"}
        >
          <bn-input
            mono
            .value=${check.claim ?? ""}
            @input=${(e: CustomEvent<{ value: string }>) => {
              e.stopPropagation();
              onChange({ ...check, claim: e.detail.value });
            }}
          ></bn-input>
        </bn-field>
        <bn-field
          label="Operator"
          required
          hint=${this.schema.help(group, "op") || this.schema.enumHelp(opField?.enum)}
          error=${opFinding?.message ?? ""}
          rule=${opFinding?.rule ?? ""}
          severity=${opFinding ? diagnosticSeverity(opFinding.severity) : "error"}
        >
          <bn-select
            .options=${this.schema.options(opField?.enum)}
            .value=${check.op ?? ""}
            placeholder="Select an operator…"
            @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
              e.stopPropagation();
              // Switching the operator drops the value fields the new operator does
              // not take, so a leftover `values` cannot survive into the YAML beside
              // an `Exists` and read as a constraint.
              onChange({ claim: check.claim, op: e.detail.value });
            }}
          ></bn-select>
        </bn-field>
      </div>
      ${this.schema.required(valueField, siblings)
        ? html`<policy-typed-value
            label="Value"
            .help=${this.schema.help(group, "value")}
            .typeOptions=${this.schema.options("ValueType")}
            .typesHelp=${this.schema.enumHelp("ValueType")}
            .value=${check.value}
            @value-change=${(e: CustomEvent<TypedValueChangeDetail>) => {
              e.stopPropagation();
              onChange({ ...check, value: e.detail.value });
            }}
          ></policy-typed-value>`
        : nothing}
      ${this.schema.required(valuesField, siblings)
        ? html`<policy-typed-value
            label="Values"
            multiple
            .minItems=${valuesField?.minItems ?? 1}
            .help=${this.schema.help(group, "values")}
            .typeOptions=${this.schema.options("ValueType")}
            .typesHelp=${this.schema.enumHelp("ValueType")}
            .values=${check.values ?? []}
            @value-change=${(e: CustomEvent<TypedValueChangeDetail>) => {
              e.stopPropagation();
              onChange({ ...check, values: e.detail.values });
            }}
          ></policy-typed-value>`
        : nothing}
      ${this.schema.required(patternField, siblings)
        ? html`<bn-field label="Pattern" required hint=${this.schema.help(group, "pattern")}>
            <bn-input
              mono
              .value=${check.pattern ?? ""}
              @input=${(e: CustomEvent<{ value: string }>) => {
                e.stopPropagation();
                onChange({ ...check, pattern: e.detail.value });
              }}
            ></bn-input>
          </bn-field>`
        : nothing}
      <bn-button size="sm" variant="ghost" @click=${onRemove}>Remove claim check</bn-button>
    </div>`;
  }

  private renderSubjects(list: ClauseList, index: number, clause: Clause) {
    const subjects = clause.subjects ?? {};
    const base = `${this.path}.${list}[${index}].subjects`;
    const write = (next: SubjectSelector) => this.patchSubjects(list, index, next);
    const apiKeyIds = [...new Set(this.bindings.flatMap((b) => b.apiKeyIds))].sort();

    return html`<div class="half">
      <div class="half-head">
        <h4>Subjects</h4>
        ${clause.subjects
          ? nothing
          : html`<span class="widening"
              >${this.schema.help("clause", "subjects") ||
              "Omit it and the clause matches every subject, including anonymous."}</span
            >`}
      </div>
      ${this.schema.fields("subjectSelector").map((field) => {
        const offer = this.schema.offer(field);
        if (offer === "hidden") return nothing;
        const path = `${base}.${field.name}`;
        if (field.name === "jwtClaims") {
          const checks = subjects.jwtClaims ?? [];
          return html`<bn-field label="JWT claim checks" hint=${field.help ?? ""}>
            <div class="checks">
              ${checks.map((c, i) =>
                this.renderClaimCheck(
                  c,
                  i,
                  path,
                  (next) => {
                    const all = [...checks];
                    all[i] = next;
                    write({ ...subjects, jwtClaims: all });
                  },
                  () => {
                    const all = checks.filter((_, j) => j !== i);
                    write(
                      all.length === 0
                        ? stripKey(subjects, "jwtClaims")
                        : { ...subjects, jwtClaims: all },
                    );
                  },
                ),
              )}
              <bn-button
                size="sm"
                @click=${() =>
                  write({ ...subjects, jwtClaims: [...checks, { claim: "", op: "" }] })}
              >
                Add claim check
              </bn-button>
            </div>
          </bn-field>`;
        }
        if (STRING_LIST_FIELDS.has(field.name)) {
          const values = (subjects[field.name as keyof SubjectSelector] as string[] | undefined) ?? [];
          const disabled = offer === "no";
          return this.renderStringList(
            labelFor(field.name),
            field.help ?? "",
            values,
            path,
            (next) => write(this.setStringList(subjects, field.name, next)),
            {
              disabled,
              badge: disabled ? this.schema.badge(field) : undefined,
              reason: disabled ? this.schema.disabledReason(field) : undefined,
              completions: field.name === "apiKeys" ? apiKeyIds : undefined,
            },
          );
        }
        return nothing;
      })}
    </div>`;
  }

  private renderResources(list: ClauseList, index: number, clause: Clause) {
    const resources = clause.resources ?? {};
    const base = `${this.path}.${list}[${index}].resources`;
    const write = (next: ResourceSelector) => this.patchResources(list, index, next);
    const grammar = this.schema.groupHelp("resourceSelector");

    return html`<div class="half">
      <div class="half-head">
        <h4>Resources</h4>
        ${clause.resources
          ? nothing
          : html`<span class="widening"
              >${this.schema.help("clause", "resources") ||
              "Omit it and the clause matches every table."}</span
            >`}
      </div>
      ${this.schema.fields("resourceSelector").map((field) => {
        const offer = this.schema.offer(field);
        if (offer === "hidden") return nothing;
        const path = `${base}.${field.name}`;
        const values = (resources[field.name as keyof ResourceSelector] as string[] | undefined) ?? [];
        const scope = PATTERN_SCOPES[field.name as keyof typeof PATTERN_SCOPES];
        if (scope) {
          return html`<policy-pattern-list
            label=${labelFor(field.name)}
            scope=${scope}
            path=${path}
            ?no-dots=${field.items?.pattern === "^[^.]+$"}
            ?disabled=${offer === "no"}
            .values=${values}
            .tables=${this.cache.tables}
            .cacheEmpty=${this.cache.degraded}
            .cacheWarning=${this.cache.warning}
            .help=${field.help ?? ""}
            .grammar=${grammar}
            .findings=${this.findings}
            @patterns-change=${(e: CustomEvent<PatternListChangeDetail>) => {
              e.stopPropagation();
              write(this.setStringList(resources, field.name, e.detail.values));
            }}
            @pattern-split=${(e: CustomEvent<PatternSplitDetail>) => {
              e.stopPropagation();
              this.applySplit(list, index, field.name, e.detail);
            }}
          ></policy-pattern-list>`;
        }
        if (field.name === "tags") {
          return this.renderTags(field.help ?? "", values, path, (next) =>
            write(this.setStringList(resources, "tags", next)),
          );
        }
        if (field.name === "actions") {
          return html`<bn-field label="Actions" hint=${field.help ?? ""}>
            <bn-select
              multiple
              .options=${this.schema.options(field.items?.enum ?? "Action")}
              .values=${values}
              placeholder="Every verb"
              @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
                e.stopPropagation();
                write(this.setStringList(resources, "actions", e.detail.values));
              }}
            ></bn-select>
          </bn-field>`;
        }
        return nothing;
      })}
      ${this.renderClauseResolution(resources)}
    </div>`;
  }

  /** The tags field. A picker when the draft's tags are known — an unknown tag is a
   * compile error that rejects the **whole** snapshot, so free text here is a way to
   * take an instance's policy offline. When they are not known the field still
   * accepts text and says exactly that. */
  private renderTags(
    help: string,
    values: string[],
    path: string,
    onChange: (values: string[]) => void,
  ) {
    if (this.availableTags.length > 0) {
      return html`<bn-field label="Tags" hint=${help}>
        <bn-select
          multiple
          filterable
          .options=${this.availableTags.map((t) => ({ value: t, label: t }))}
          .values=${values}
          placeholder="No tag constraint"
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            e.stopPropagation();
            onChange(e.detail.values);
          }}
        ></bn-select>
      </bn-field>`;
    }
    return this.renderStringList("Tags", help, values, path, onChange, {});
  }

  /** The clause-level resolution preview: what the catalog, schema and table
   * patterns jointly cover. Columns, tags and actions are deliberately not folded
   * in — columns do not constrain table matching, tags expand through another
   * object's rules, and actions are checked above the selector. */
  private renderClauseResolution(resources: ResourceSelector) {
    const constrained =
      (resources.catalogs?.length ?? 0) +
      (resources.schemas?.length ?? 0) +
      (resources.tables?.length ?? 0);
    if (constrained === 0 || this.cache.degraded) return nothing;
    const { names, total } = resolveClauseTables(resources, this.cache.tables);
    if (total === 0) {
      return html`<p class="resolved none" role="status">
        This clause resolves to no table at all, so it protects nothing.
        <span class="advisory">Advisory preview — the instance's engine decides.</span>
      </p>`;
    }
    return html`<p class="resolved" role="status">
      This clause currently covers ${total} ${total === 1 ? "table" : "tables"}: ${names.join(", ")}${total >
      names.length
        ? ` (first ${names.length} shown)`
        : ""}.
      <span class="advisory"
        >Advisory preview — the instance's engine decides. It reflects the schema at the cache
        timestamp.</span
      >
    </p>`;
  }

  /**
   * applySplit distributes a dotted value across catalogs, schemas and tables and
   * removes the dotted original (`SC-F507`). Only this element can do it: the three
   * lists live on the same resource selector, and the field that raised the offer
   * owns just one of them.
   */
  private applySplit(list: ClauseList, index: number, field: string, detail: PatternSplitDetail) {
    const clause = this.selector[list]?.[index] ?? {};
    const resources: Record<string, unknown> = { ...(clause.resources ?? {}) };
    const remaining = ((resources[field] as string[] | undefined) ?? []).filter(
      (v) => v !== detail.value,
    );
    const merged = (key: string, value: string) => {
      if (!value) return;
      const current = (resources[key] as string[] | undefined) ?? [];
      if (!current.includes(value)) resources[key] = [...current, value];
    };
    if (remaining.length === 0) delete resources[field];
    else resources[field] = remaining;
    merged("catalogs", detail.split.catalog);
    merged("schemas", detail.split.schema);
    merged("tables", detail.split.table);
    this.patchResources(list, index, resources as ResourceSelector);
  }

  private renderClause(list: ClauseList, clause: Clause, index: number) {
    const total = (this.selector[list] ?? []).length;
    const subtree = findingsUnder(this.findings, `${this.path}.${list}[${index}]`);
    const badge = worst(subtree);
    return html`<div class="clause">
      <div class="clause-head">
        <span class="clause-title">Clause ${index + 1} of ${total}</span>
        ${badge
          ? html`<bn-badge variant=${badge.severity === "error" ? "error" : "warning"}
              >${badge.rule}</bn-badge
            >`
          : nothing}
        <span class="spacer"></span>
        <bn-button
          size="sm"
          variant="ghost"
          @click=${() => this.removeClause(list, index)}
          aria-label=${`Remove clause ${index + 1}`}
        >
          Remove clause
        </bn-button>
      </div>
      ${!clause.subjects && !clause.resources
        ? html`<p class="note">
            This clause constrains nothing, so it matches every subject — including anonymous — on
            every table.
          </p>`
        : nothing}
      ${this.renderSubjects(list, index, clause)} ${this.renderResources(list, index, clause)}
    </div>`;
  }

  private renderClauseList(list: ClauseList) {
    const clauses = this.selector[list] ?? [];
    return html`<div class="clauses">
      ${clauses.map((c, i) => this.renderClause(list, c, i))}
      <div>
        <bn-button size="sm" @click=${() => this.addClause(list)}>
          Add ${list === "any" ? "an alternative" : "a required"} clause
        </bn-button>
      </div>
    </div>`;
  }

  /** The both-populated banner. It states the semantics rather than hiding them,
   * and it offers the fix in both directions because only the author knows which
   * list they meant. */
  private renderBothBanner() {
    const anyCount = (this.selector.any ?? []).length;
    const allCount = (this.selector.all ?? []).length;
    return html`<div class="banner" role="alert">
      <p>
        Both lists are populated. Sluice evaluates that as
        <strong>ANY(any) OR ALL(all)</strong> — an OR between the two, not an AND — so this selector
        matches if any one of the ${anyCount} alternative clauses matches, <em>or</em> if all
        ${allCount} required clauses match. That is wider than either list alone and is almost never
        what an author draws, so this shape cannot be published.
      </p>
      <div class="row">
        <bn-button size="sm" @click=${() => this.merge("any")}>
          Merge all ${anyCount + allCount} into "match any"
        </bn-button>
        <bn-button size="sm" @click=${() => this.merge("all")}>
          Merge all ${anyCount + allCount} into "match all"
        </bn-button>
      </div>
    </div>`;
  }

  /** The prose restatement `SC-S503` asks for. It is generated from the object
   * rather than written, so it cannot drift from what will be published. */
  private summarize(): string {
    const list = this.mode;
    const clauses = this.selector[list] ?? [];
    if (clauses.length === 0) {
      return this.isExclude
        ? "Nothing is excluded."
        : "Nothing is matched yet. A selector with neither list populated matches nothing and is refused at decode.";
    }
    const parts = clauses.map((c) => describeClause(c));
    const joiner = list === "any" ? ", OR " : ", AND ";
    const lead = this.isExclude ? "Drops the policy when " : "Matches when ";
    return `${lead}${parts.join(joiner)}.`;
  }

  override render() {
    if (!this.schema) return nothing;
    const selectorHelp = this.schema.groupHelp("selector");
    const emptyExclude =
      this.isExclude && (this.selector.any ?? []).length === 0 && (this.selector.all ?? []).length === 0;
    return html`
      <div class="mode">
        <fieldset>
          <legend>How the clauses combine</legend>
          <label class="radio">
            <input
              type="radio"
              name=${`${this.path}-mode`}
              .checked=${this.mode === "any"}
              @change=${() => this.setMode("any")}
            />
            Match ANY clause
          </label>
          <label class="radio">
            <input
              type="radio"
              name=${`${this.path}-mode`}
              .checked=${this.mode === "all"}
              @change=${() => this.setMode("all")}
            />
            Match ALL clauses
          </label>
        </fieldset>
      </div>
      ${selectorHelp ? html`<p class="note">${selectorHelp}</p>` : nothing}
      ${this.scopeExplainer ? html`<p class="note">${this.scopeExplainer}</p>` : nothing}
      ${this.bothPopulated ? this.renderBothBanner() : nothing}
      ${this.bothPopulated
        ? html`${this.renderClauseList("any")}${this.renderClauseList("all")}`
        : this.renderClauseList(this.mode)}
      ${emptyExclude
        ? html`<p class="note">
            An exclusion with no clauses is accepted by Sluice and is permanently false, so it
            excludes nothing. Leave it empty only if you mean to remove it.
          </p>`
        : nothing}
      <p class="summary">${this.summarize()}</p>
    `;
  }
}

/** labelFor is the visible label for a schema field name. The schema carries help
 * text but no label for a field, so the mapping is here — one place, and the field
 * name is the fallback so a schema addition renders rather than disappearing. */
function labelFor(name: string): string {
  const labels: Record<string, string> = {
    groups: "Groups",
    roles: "Roles",
    apiKeys: "API key ids",
    ipRanges: "Source IP ranges",
    catalogs: "Catalogs",
    schemas: "Schemas",
    tables: "Tables",
    columns: "Columns",
    tags: "Tags",
    actions: "Actions",
  };
  return labels[name] ?? name;
}

/** stripKey removes one key without mutating the source, keeping the holder's own
 * type so a closed shape does not have to be widened to drop a field from it. */
function stripKey<T extends object>(source: T, key: string): T {
  const next = { ...source } as Record<string, unknown>;
  delete next[key];
  return next as T;
}

/** describeClause restates one clause in prose. */
function describeClause(clause: Clause): string {
  const parts: string[] = [];
  const s = clause.subjects;
  if (s) {
    if (s.groups?.length) parts.push(`the subject is in ${quoteList(s.groups)}`);
    if (s.roles?.length) parts.push(`the subject has role ${quoteList(s.roles)}`);
    if (s.apiKeys?.length) parts.push(`the subject id is ${quoteList(s.apiKeys)}`);
    if (s.ipRanges?.length) parts.push("a source IP range is set but is not enforced by this build");
    for (const c of s.jwtClaims ?? []) {
      parts.push(`claim ${c.claim || "?"} ${humanOp(c.op)} ${describeValue(c)}`);
    }
  } else {
    parts.push("any subject, including anonymous");
  }
  const r = clause.resources;
  if (r) {
    if (r.catalogs?.length) parts.push(`the catalog matches ${quoteList(r.catalogs)}`);
    if (r.schemas?.length) parts.push(`the schema matches ${quoteList(r.schemas)}`);
    if (r.tables?.length) parts.push(`the table matches ${quoteList(r.tables)}`);
    if (r.tags?.length) parts.push(`the column carries tag ${quoteList(r.tags)}`);
    if (r.columns?.length) parts.push(`the column matches ${quoteList(r.columns)} (columns do not constrain table matching)`);
    if (r.actions?.length) parts.push(`the statement is ${r.actions.join(" or ")}`);
  } else {
    parts.push("any table");
  }
  return parts.join(" AND ");
}

function quoteList(values: string[]): string {
  return values.map((v) => `\`${v}\``).join(" or ");
}

function humanOp(op: string): string {
  return op === "" ? "(no operator)" : op.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
}

function describeValue(check: ClaimCheck): string {
  if (check.pattern) return `\`${check.pattern}\``;
  if (check.values?.length) return check.values.map(literal).join(" or ");
  if (check.value !== undefined) return literal(check.value);
  return "";
}

function literal(v: ScalarValue): string {
  return typeof v === "string" ? `\`${v}\`` : String(v);
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-selector": PolicySelector;
  }
}
